import { Link as RouterLink, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Link from '@mui/material/Link'
import Badge from '@mui/material/Badge'
import AvatarPopper from 'components/smart/header/components/AvatarPopper'
import NotificationsPopover from 'components/smart/header/components/NotificationsPopover'
import AdxBeta from 'components/custom/AdxBeta'
import { Chat, ChatOutlined, SpaceDashboardOutlined, Apps, Mail, NotificationsNone } from '@mui/icons-material'
import AdxButton from 'components/custom/AdxButton'
import AdxToolTip from 'components/custom/AdxToolTip'
import { Menu, IconButton, Tooltip, Typography, Chip } from '@mui/material'
import { useEffect, useRef, useState, useContext } from 'react'
import {
  useTotalUnreadCount,
  useConversations,
  useCurrentUser,
  getConversationsCacheKey,
} from 'services/api/chat/chatHooks'
// import { useChatWebSocket } from 'services/hooks/useChatWebSocket'
import usePushNotifications from 'services/hooks/usePushNotifications'
import { useQueryClient } from 'react-query'
import { authContext } from 'contexts/AuthContext'
import { useNotifications } from 'services/api/notification/notificationHooks'
import { toast } from 'react-toastify'

const Header = ({
  withHeader,
  title,
  stickyHeader = false,
  isBeta,
  childrenMaxWidth,
  menuShown,
  setMenuShown,
}) => {
  const pathname = useLocation()
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const queryClient = useQueryClient()
  const notificationSoundRef = useRef(null)
  const titleBlinkIntervalRef = useRef(null)
  const originalTitleRef = useRef('adnexio | HR')
  const unreadMessagesRef = useRef(new Map()) // Store unread messages: conversationId -> { senderName, count }
  const { auth } = useContext(authContext)

  // ============================================
  // PUSH NOTIFICATIONS (Pusher Channels)
  // ============================================
  // usePushNotifications(
  //   auth,
  //   // Handler for new announcements (Channel 1a & 1b)
  //   (payload) => {
  //     queryClient.invalidateQueries('announcements')
  //     toast(
  //       <Box>
  //         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
  //           {payload.title}
  //         </Typography>
  //         {payload.body && (
  //           <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.75rem' }}>
  //             {payload.body}
  //           </Typography>
  //         )}
  //       </Box>,
  //       {
  //         toastId: `announcement-${payload.id}`,
  //         position: 'top-right',
  //         autoClose: 4000,
  //         style: { backgroundColor: '#F7F9FA', borderLeft: '4px solid #F3BF3A' },
  //       }
  //     )
  //   },
  //   // Handler for personal notifications (Channel 2 — staff.{staffId})
  //   (payload) => {
  //     queryClient.invalidateQueries('notifications')
  //     toast(
  //       <Box>
  //         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
  //           {payload.title}
  //         </Typography>
  //         {payload.message && (
  //           <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.75rem' }}>
  //             {payload.message}
  //           </Typography>
  //         )}
  //       </Box>,
  //       {
  //         toastId: payload.notification_id,
  //         position: 'top-right',
  //         autoClose: 4000,
  //         style: { backgroundColor: '#f7f9fa', borderLeft: '4px solid #f3bf3a' },
  //       }
  //     )
  //   }
  // )

  // ============================================
  // GLOBAL USER & CONVERSATIONS FETCH
  // ============================================
  // Get current user for WebSocket and notifications
  const { data: currentUserData } = useCurrentUser()
  const currentUser = currentUserData?.user

  // ============================================
  // APP LAUNCHER VISIBILITY CHECK
  // ============================================
  // Show app launcher:
  // - Always in development
  // - Always in staging
  // - Only for @invokeisdata.com users and demo accounts in production
  const shouldShowAppLauncher = () => {
    const isDevelopment = process.env.NODE_ENV === 'development'
    const isStaging = typeof window !== 'undefined' && window.location.hostname.includes('staging')
    const isProduction = process.env.NODE_ENV === 'production' && !isStaging

    // Always show in development and staging
    if (isDevelopment || isStaging) {
      return true
    }

    // In production, only show for internal users and demo accounts.
    if (isProduction) {
      const userEmail = (auth?.data?.staff?.work_email || '').toLowerCase()
      return userEmail.endsWith('@invokeisdata.com') || userEmail.startsWith('demo.')
    }

    // Default to false for safety
    return false
  }

  const showAppLauncher = shouldShowAppLauncher()

  // Fetch conversations globally so badge works on all pages
  // This runs on all pages where Header is rendered
  // Silent fetch - no loading UI needed in header
  useConversations()

  // Get total unread count for badge (reads from cache, no API calls)
  const { data: totalUnreadCount = 0 } = useTotalUnreadCount()

  // ============================================
  // GLOBAL WEBSOCKET CONNECTION
  // ============================================
  // Connect to WebSocket globally (not just on chat page)
  // This enables real-time badge updates on ALL pages
  // const { addEventListener } = useChatWebSocket(null, currentUser?.user_uuid)

  // Initialize notification sound
  useEffect(() => {
    notificationSoundRef.current = new Audio(
      'https://adnexiohr-assets.adnexio.jobs/notification.mp3'
    )
    notificationSoundRef.current.volume = 0.5 // 50% volume
  }, [])

  // ============================================
  // TAB TITLE BLINKING LOGIC
  // ============================================

  // Start blinking title when there are unread messages
  const startTitleBlink = () => {
    // Don't blink if already blinking or no unread messages
    if (titleBlinkIntervalRef.current || unreadMessagesRef.current.size === 0) return

    let isOriginalTitle = true

    titleBlinkIntervalRef.current = setInterval(() => {
      if (unreadMessagesRef.current.size === 0) {
        stopTitleBlink()
        return
      }

      if (isOriginalTitle) {
        // Calculate total unread count
        let totalCount = 0
        let firstSender = ''

        for (const [, data] of unreadMessagesRef.current) {
          totalCount += data.count
          if (!firstSender) firstSender = data.senderName
        }

        // Show notification title
        if (unreadMessagesRef.current.size === 1) {
          // Single conversation
          document.title = `💬 ${firstSender} sent you a message`
        } else {
          // Multiple conversations
          document.title = `💬 ${totalCount} new messages`
        }
      } else {
        // Show original title
        document.title = originalTitleRef.current
      }

      isOriginalTitle = !isOriginalTitle
    }, 1000) // Blink every 1 second

    console.log('📍 Tab title blinking started')
  }

  // Stop blinking and restore original title
  const stopTitleBlink = () => {
    if (titleBlinkIntervalRef.current) {
      clearInterval(titleBlinkIntervalRef.current)
      titleBlinkIntervalRef.current = null
      document.title = originalTitleRef.current
      console.log('📍 Tab title blinking stopped')
    }
  }

  // Add unread message to tracking
  const addUnreadMessage = (conversationId, senderName) => {
    const existing = unreadMessagesRef.current.get(conversationId)

    if (existing) {
      existing.count += 1
    } else {
      unreadMessagesRef.current.set(conversationId, {
        senderName,
        count: 1,
      })
    }

    startTitleBlink()
  }

  // Remove unread messages for a conversation (when read)
  const clearUnreadForConversation = (conversationId) => {
    if (unreadMessagesRef.current.has(conversationId)) {
      unreadMessagesRef.current.delete(conversationId)
      console.log('📍 Cleared unread for conversation:', conversationId)

      // Stop blinking if no more unread messages
      if (unreadMessagesRef.current.size === 0) {
        stopTitleBlink()
      }
    }
  }

  // Clear all unread messages
  const clearAllUnread = () => {
    unreadMessagesRef.current.clear()
    stopTitleBlink()
  }

  // ============================================
  // REAL-TIME BADGE UPDATES & NOTIFICATIONS
  // ============================================
  // useEffect(() => {
  //   if (!currentUser?.user_uuid) return

  //   // Listen for new messages globally
  //   const cleanupNewMessage = addEventListener('newMessage', (data) => {
  //     console.log('🔔 Global new message event:', data)

  //     // ✅ Extract sender UUID from nested message object or root level
  //     // WebSocket event structure can vary: data.message.sender_uuid or data.sender_uuid
  //     const senderUuid = data.message?.sender_uuid || data.sender_uuid
  //     const senderName =
  //       data.message?.sender_name || data.sender_name || data.sender?.name || 'Someone'
  //     const conversationId =
  //       data.message?.conversation_uuid || data.conversation_uuid || data.conversation_id

  //     // Check if message is from another user (not from current user)
  //     const isFromOtherUser = senderUuid && senderUuid !== currentUser.user_uuid

  //     console.log('🔍 Sender UUID:', senderUuid)
  //     console.log('🔍 Current user UUID:', currentUser.user_uuid)
  //     console.log('🔍 Is from other user?', isFromOtherUser)

  //     if (isFromOtherUser) {
  //       // ✨ OPTIMIZATION: Direct cache update instead of invalidation for instant UI update
  //       const messageContent = data.message?.content || data.content || ''
  //       const messageTimestamp =
  //         data.message?.created_at || data.created_at || new Date().toISOString()
  //       const messageType = data.message?.type || data.type || 'text'
  //       const messageMedia = data.message?.media || data.media || []

  //       // Helper function to generate preview text based on message type
  //       const getMessagePreview = () => {
  //         // Check if it's a media message
  //         if (messageType === 'file' && messageMedia.length > 0) {
  //           if (messageMedia.length === 1) {
  //             return 'Sent an attachment'
  //           } else {
  //             return `Sent ${messageMedia.length} attachments`
  //           }
  //         }
  //         // For text messages
  //         return messageContent
  //       }

  //       const previewText = getMessagePreview()

  //       queryClient.setQueryData(getConversationsCacheKey(), (oldData) => {
  //         if (!oldData?.data) {
  //           // If no cache exists, fallback to invalidation
  //           queryClient.invalidateQueries(['conversations'])
  //           return oldData
  //         }

  //         console.log('📝 Updating conversations cache directly for conversation:', conversationId)

  //         return {
  //           ...oldData,
  //           data: oldData.data.map((conv) => {
  //             const convId = conv.conversation_uuid || conv.uuid || conv.id
  //             if (convId === conversationId) {
  //               return {
  //                 ...conv,
  //                 last_message: previewText,
  //                 last_message_content: previewText,
  //                 last_message_type: messageType,
  //                 last_message_sender_uuid: senderUuid,
  //                 last_message_sender: senderName,
  //                 last_message_at: messageTimestamp,
  //                 updated_at: messageTimestamp,
  //                 sort_ts: messageTimestamp,
  //                 unread_count: (conv.unread_count || 0) + 1,
  //                 is_unread: true,
  //               }
  //             }
  //             return conv
  //           }),
  //         }
  //       })

  //       // ✨ Trigger totalUnreadCount recalculation (it reads from conversations cache)
  //       queryClient.invalidateQueries(['totalUnreadCount'])

  //       // Add to unread tracking for tab title blinking
  //       addUnreadMessage(conversationId, senderName)

  //       // Play notification sound only if NOT on chat page
  //       // (Chat page has its own sound handling)
  //       if (pathname.pathname !== '/app/chat' && notificationSoundRef.current) {
  //         notificationSoundRef.current.play().catch((error) => {
  //           console.log('Could not play notification sound:', error)
  //         })
  //       }

  //       console.log('✅ Badge updated instantly via direct cache update')
  //     } else {
  //       console.log('⏭️  Skipping notification - message from current user or no sender')
  //     }
  //   })

  //   // Listen for read receipts to update badge and stop blinking
  //   const cleanupReadReceipt = addEventListener('readReceipt', (data) => {
  //     console.log('📖 Read receipt event:', data)

  //     // ✨ OPTIMIZATION: Direct cache update for instant UI update
  //     if (data.user_uuid === currentUser.user_uuid && data.conversation_uuid) {
  //       queryClient.setQueryData(getConversationsCacheKey(), (oldData) => {
  //         if (!oldData?.data) {
  //           // If no cache exists, fallback to invalidation
  //           queryClient.invalidateQueries(['conversations'])
  //           return oldData
  //         }

  //         console.log('📝 Clearing unread count for conversation:', data.conversation_uuid)

  //         return {
  //           ...oldData,
  //           data: oldData.data.map((conv) => {
  //             const convId = conv.conversation_uuid || conv.uuid || conv.id
  //             if (convId === data.conversation_uuid) {
  //               return {
  //                 ...conv,
  //                 unread_count: 0,
  //                 is_unread: false,
  //               }
  //             }
  //             return conv
  //           }),
  //         }
  //       })

  //       // ✨ Trigger totalUnreadCount recalculation
  //       queryClient.invalidateQueries(['totalUnreadCount'])

  //       // Clear unread for tab title blinking
  //       clearUnreadForConversation(data.conversation_uuid)
  //     } else {
  //       // For other users' read receipts, we might still want to invalidate
  //       // to get the latest read status
  //       queryClient.invalidateQueries(['conversations'])
  //       queryClient.invalidateQueries(['totalUnreadCount'])
  //     }
  //   })

  //   return () => {
  //     cleanupNewMessage()
  //     cleanupReadReceipt()
  //   }
  // }, [addEventListener, currentUser?.user_uuid, pathname.pathname, queryClient])

  // ============================================
  // STOP BLINKING WHEN USER VISITS CHAT PAGE
  // ============================================
  useEffect(() => {
    // When user navigates to chat page, stop blinking
    // (they're likely reading their messages)
    if (pathname.pathname === '/app/chat') {
      clearAllUnread()
    }
  }, [pathname.pathname])

  // ============================================
  // STOP BLINKING ON PAGE VISIBILITY CHANGE
  // ============================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User came back to the tab
        // Keep blinking if there are still unread messages
        if (unreadMessagesRef.current.size > 0 && !titleBlinkIntervalRef.current) {
          startTitleBlink()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // ============================================
  // CLEANUP ON UNMOUNT
  // ============================================
  useEffect(() => {
    return () => {
      stopTitleBlink()
    }
  }, [])

  // Auto-close menu when navigating to chat
  useEffect(() => {
    if (pathname.pathname === '/app/chat') {
      setMenuShown(false)
    }
  }, [pathname.pathname, setMenuShown])

  // const [notificationCount, setNotificationCount] = useState(0)
  // const [notificationMessage, setNotificationMessage] = useState()
  const [appLauncherAnchorEl, setAppLauncherAnchorEl] = useState(null)
  // const [notificationAnchorEl, setNotificationAnchorEl] = useState(null)
  // const [notificationCount] = useState(5) // TODO: replace with real notification data/hook
  // const notificationPopoverOpen = Boolean(notificationAnchorEl)
  const appLauncherOpen = Boolean(appLauncherAnchorEl)

  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null)
  const notificationPopoverOpen = Boolean(notificationAnchorEl)

  const { data: notificationData } = useNotifications({ page: 1, perPage: 5 })
  const notificationCount =
    notificationData?.items?.filter((item) => item.isUnread).length || 0

  // useEffect(() => {
  //   setNotificationCount(0)
  //   setNotificationMessage('adnexio | HR')
  // }, [])

  // const notificationHandler = () => {
  //   //temporary function
  //   Bugsnag.notify(new Error('This is a test bugsnag error from notification icon.'))
  // }

  const styles = {
    menuItem: {
      cursor: 'pointer',
      height: 45,
      display: 'flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
      width: 'fit-content',
    },
    selectedMenuItem: {
      bgcolor: 'primary.light',
    },
    iconItem: {
      height: 35,
      width: 35,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    selectedIconItem: {
      bgcolor: 'primary.main',
      borderRadius: '100%',
    },
    hoverEffect: {
      ':hover': { bgcolor: 'gray-menu-hover.main' },
    },
  }

  const MenuItem = ({ isSelected, onClick, children, label }) => (
    <AdxToolTip title={label} placement="bottom" arrow>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={0.75}
        borderRadius={'500px'}
        {...(isSelected && styles.selectedMenuItem)}
        color={isSelected ? 'primary.contrastText' : '#65676b'}
        sx={{
          ...styles.menuItem,
          ':hover': {
            bgcolor: 'primary.hover',
            '& > div': {
              // Target the IconItem child
              bgcolor: 'primary.hover',
            },
          },
        }}
        onClick={onClick}
      >
        {children}
      </Box>
    </AdxToolTip>
  )

  const IconItem = ({ item, isSelected, children }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderRadius={'100%'}
      {...(isSelected && styles.selectedIconItem)}
      color="#65676b"
      sx={{
        ...styles.iconItem,
        transition: 'background-color 0.2s', // Add transition for smooth hover
      }}
    >
      {children}
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: isLarge ? '91px' : '60px',
        width: '100%',
        p: isLarge ? 3 : 2,
        position: 'sticky',
        top: 0,
        bgcolor: isLarge ? 'gray-background.main' : 'white.main',
        borderBottom: isLarge ? 0 : 1,
        borderColor: 'gray-line.main',
        zIndex: 49,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          gap: 2,
        }}
      >
        <SpaceDashboardOutlined
          sx={{ cursor: 'pointer' }}
          onClick={() => setMenuShown(menuShown === true ? false : true)}
        />
        <Box sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'flex' }, fontSize: '16px' }}>
          {title}
        </Box>
        {isBeta && <AdxBeta />}
      </Box>

      <Link
        component={RouterLink}
        to="/app/homepage"
        sx={{ position: 'absolute', left: '50%', translate: '-50%' }}
      >
        <Box
          as="img"
          src={!isLarge ? '/adnexio-logo-icon.svg' : '/adnexio-logo-text.svg'}
          height={20}
          sx={{ display: { xs: 'block', lg: 'none' }, cursor: 'pointer' }}
        />
      </Link>
      <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 2 }, alignItems: 'center' }}>
        {/* <Link component={RouterLink} to="/app/chat">
          <MenuItem isSelected={pathname.pathname === '/app/chat'} label={'Chat'}>
            <Badge
              badgeContent={totalUnreadCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.625rem',
                  height: '18px',
                  minWidth: '18px',
                  padding: '0 4px',
                  fontWeight: 600,
                  top: 2,
                  right: 2,
                },
              }}
            >
              <IconItem isSelected={true}>
                <ChatOutlined sx={{ fontSize: 20 }} />
              </IconItem>
            </Badge>
          </MenuItem>
        </Link> */}
        {showAppLauncher && (
          <>
            <Tooltip title="Adnexio apps">
              <IconButton
                onClick={(event) => setAppLauncherAnchorEl(event.currentTarget)}
                sx={{
                  padding: { xs: 0.5, md: 1 },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                aria-label="Open app launcher"
              >
                <Apps sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={appLauncherAnchorEl}
              open={appLauncherOpen}
              onClose={() => setAppLauncherAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 280,
                  borderRadius: 2,
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                  }}
                >
                  <Box
                    component="button"
                    onClick={() => {
                      // Determine Chat URL based on environment
                      let chatUrl;
                      if (process.env.NODE_ENV === 'development') {
                        chatUrl = 'http://localhost:3002';
                      } else {
                        // Staging and production both use the same URL
                        chatUrl = 'https://chat.adnexio.jobs';
                      }
                      window.open(chatUrl, '_self');
                      setAppLauncherAnchorEl(null);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        backgroundColor: '',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <img
                        src="/adnexio-logo.png"
                        alt="adnexio Chat"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '8px',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textAlign: 'center',
                          color: 'text.primary',
                        }}
                      >
                        adnexio Chat
                      </Typography>
                      <Chip
                        label="BETA"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor: 'grey.300',
                          color: 'text.primary',
                          '& .MuiChip-label': {
                            px: 0.75,
                          },
                        }}
                      />
                    </Box>

                  </Box>
                </Box>
              </Box>
            </Menu>
          </>
        )}
        <Tooltip title="Notifications">
          <IconButton
            onClick={(event) => setNotificationAnchorEl(event.currentTarget)}
            sx={{
              padding: { xs: 0.5, md: 1 },
              color: '#65676b',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            aria-label="Notifications"
          >
            <Badge
              badgeContent={notificationCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.625rem',
                  height: '18px',
                  minWidth: '18px',
                  padding: '0 4px',
                  fontWeight: 600,
                  top: 2,
                  right: 2,
                },
              }}
            >
              <NotificationsNone sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <NotificationsPopover
          anchorEl={notificationAnchorEl}
          open={notificationPopoverOpen}
          onClose={() => setNotificationAnchorEl(null)}
        />
        <AvatarPopper />
      </Box >
    </Box >
  )
}

export default Header
